import { Icon, SkeletonLoader } from '@toggle/design-system';
import { Alert } from '@toggle/toggle';
import classnames from 'classnames';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { useConversationTask } from '~/react-query-hooks/alfred/useConversationTask';
import { getCardDate } from '~/utils/card-utils/card-utils';

import { alertsCardLoaderAreas } from '../../AlertsCard';
import sharedStyles from '../../AlertsCard.module.scss';
import { extractContentPreview } from '../../utils/extract-content.preview';
import * as S from './AlertsAlfredCard.styles';

interface AlertsAlfredCardProps {
  alertEvent: Alert;
  dropdown?: React.ReactNode;
}

export const AlertsAlfredCard = ({
  alertEvent,
  dropdown,
}: AlertsAlfredCardProps) => {
  const { t } = useTranslation('alerts');
  const { created_at: createdAt, source_id: sourceId, read } = alertEvent;

  const { data: conversationData, isLoading } = useConversationTask(sourceId);
  const taskState = conversationData?.tasks?.[0]?.last_state;

  const isError =
    taskState?.status === 'Error' || taskState?.status === 'Fatal';

  if (isLoading) {
    return <SkeletonLoader areas={alertsCardLoaderAreas} />;
  }

  if (!taskState) {
    return null;
  }

  return (
    <>
      <div className={sharedStyles.header}>
        <div className={sharedStyles.headerLeft}>
          <span>{t('alerts:alfredCard.title')}</span>
        </div>
        <div className={sharedStyles.headerRight}>
          <span className={sharedStyles.dateSpan}>
            {getCardDate(new Date(createdAt))}
          </span>
          {dropdown}
        </div>
      </div>
      <div className={sharedStyles.bodyWrapper}>
        <div className={sharedStyles.logoWrapper}>
          <S.AlfredLogo>
            <Icon iconName="Alfred" size={20} fillColor="var(--icon-primary)" />
          </S.AlfredLogo>
        </div>
        <div className={sharedStyles.textContent}>
          <p
            className={classnames(sharedStyles.primaryText, {
              [sharedStyles.read]: read,
            })}
          >
            {conversationData?.name}
          </p>
          <S.SecondaryText>
            {isError ? (
              <S.ErrorWrapper>
                <Icon
                  fillColor={read ? 'var(--icon-soft)' : 'var(--icon-warning)'}
                  iconName="InformationCircle"
                  size={12}
                />
                <S.ErrorText $read={read}>
                  {t('alerts:alfredCard.errorText')}
                </S.ErrorText>
              </S.ErrorWrapper>
            ) : (
              extractContentPreview(taskState?.content ?? '')
            )}
          </S.SecondaryText>
        </div>
      </div>
    </>
  );
};
